import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firestore";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { useUser } from "../context/userContext";
import { usePremiumCheck } from './hooks/usePremiumCheck';
import { Headphones } from "lucide-react";
import Header from "../Components/Header";

const AudioLessonsList = () => {
  const [audioLessons, setAudioLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { premium: userIsPremium, id: userId, loading: userLoading, userType } = useUser();
  const { isPremium } = usePremiumCheck(userId, userType);

  const effectivePremium = isPremium || userIsPremium;

  // Telegram WebApp инициализация (только для Telegram пользователей)
  useEffect(() => {
    if (userType === 'telegram' && window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      
      window.Telegram.WebApp.BackButton.isVisible = true;
      window.Telegram.WebApp.BackButton.offClick();
      window.Telegram.WebApp.BackButton.onClick(() => {
        console.log('Back button clicked');
        navigate(-1);
      });
    }

    return () => {
      if (userType === 'telegram' && window.Telegram?.WebApp) {
        window.Telegram.WebApp.BackButton.isVisible = false;
        window.Telegram.WebApp.BackButton.offClick();
      }
    };
  }, [navigate, userType]);

  const BASE_URL = "https://artdownload2-production.up.railway.app";

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
    
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    const loadAudioLessons = async () => {
      try {
        const audioRef = collection(db, "audioLessons");
        const q = query(audioRef, orderBy("createdAt", "desc"));
        const snap = await getDocs(q);

        const items = snap.docs.map((d) => {
          const data = d.data();
          
          let imageUrl = data.image;
          if (imageUrl && imageUrl.startsWith('/')) {
            imageUrl = `${BASE_URL}${imageUrl}`;
          }
          
          let audioFiles = [];
          if (data.audioFiles && Array.isArray(data.audioFiles)) {
            audioFiles = data.audioFiles.map(audio => {
              if (typeof audio === 'string') {
                return audio.startsWith('/') ? `${BASE_URL}${audio}` : audio;
              } else if (audio && audio.url) {
                return {
                  ...audio,
                  url: audio.url.startsWith('/') ? `${BASE_URL}${audio.url}` : audio.url
                };
              }
              return audio;
            });
          }
          
          return {
            id: d.id,
            ...data,
            image: imageUrl,
            audioFiles: audioFiles
          };
        });

        setAudioLessons(items);
      } catch (e) {
        console.error("Ошибка загрузки аудио уроков:", e);
      } finally {
        setLoading(false);
      }
    };

    loadAudioLessons();
  }, []);

  const hasAccessToLesson = (lesson) => {
    if (!lesson.premium) return true;
    if (!userId) return false;
    return effectivePremium;
  };

  const handleLessonClick = (lesson) => {
    const hasAccess = hasAccessToLesson(lesson);
    
    if (hasAccess) {
      navigate(`/AudioLessonsPage/${lesson.id}`, { state: { lesson } });
    } else if (!userId) {
      alert("Авторизуйтесь для доступа");
    } else {
      alert("Этот аудио урок доступен только для Premium пользователей");
    }
  };

  if (loading || userLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center text-white relative">
        <div
          className="fixed inset-0"
          style={{
            backgroundImage: "url('/bghome.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            zIndex: -1
          }}
        />
        <div className="relative z-10">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full text-white relative">
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: -1
        }}
      />

      <div className="fixed bottom-0 left-0 right-0 h-[150px] bg-[radial-gradient(circle_at_center,rgba(120,0,255,0.15),transparent_70%)] z-0" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="pt-0">
          <Header />
        </div>
        
        <div 
          className="flex-1 px-4 pt-6 pb-24 overflow-y-auto"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <style>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          <h1 className="text-white text-[18px] font-semibold mb-6 flex items-center gap-2">
            МЕДИТАЦИИ И ПРАКТИКИ
          </h1>

          <div className="flex flex-col gap-4">
            {audioLessons.length === 0 ? (
              <div className="text-center text-white/60 py-8">
                Пока нет аудио уроков
              </div>
            ) : (
              audioLessons.map((lesson) => {
                const hasAccess = hasAccessToLesson(lesson);
                const isLocked = lesson.premium && !hasAccess;
                const audioCount = lesson.audioFiles?.length || 0;
                
                return (
                  <div
                    key={lesson.id}
                    className={`w-full rounded-[20px] border p-4 flex items-center gap-4 ${isLocked ? 'opacity-70' : ''}`}
                    style={{
                      background: "#241352",
                      borderColor: isLocked ? "#EF4444" : "rgba(168, 85, 247, 0.3)"
                    }}
                  >
                    <div className="w-[72px] h-[72px] rounded-[12px] overflow-hidden shrink-0 bg-gray-800">
                      {lesson.image ? (
                        <img
                          src={lesson.image}
                          alt={lesson.title || "Аудио урок"}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error("Ошибка загрузки изображения:", lesson.image);
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `
                              <div class="w-full h-full flex items-center justify-center bg-gray-800 text-gray-400">
                                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path>
                                </svg>
                              </div>
                            `;
                          }}
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800 text-gray-400">
                          <Headphones className="w-8 h-8" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 flex flex-col justify-between min-h-[72px]">
                      <div>
                        <div className="flex items-center gap-2">
                          <div
                            className="text-[16px] leading-tight font-medium break-words whitespace-normal"
                            style={{ color: isLocked ? "#D1D5DB" : "#C8AFE8" }}
                          >
                            {lesson.title || "Аудио урок"}
                          </div>
                          {lesson.premium && (
                            <span className={`px-2 py-0.5 rounded-full text-xs ${hasAccess ? 'bg-yellow-500/20 text-yellow-300' : 'bg-red-500/20 text-red-300'}`}>
                              Premium
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-1">
                          <div
                            className="text-[14px] font-medium"
                            style={{ color: isLocked ? "#EF4444" : "#34D3FF" }}
                          >
                            {isLocked ? "Заблокировано" : "Доступно"}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <button
                          className={`px-6 py-2 rounded-full text-white text-[15px] font-medium active:scale-95 transition ${isLocked ? 'cursor-not-allowed' : 'hover:opacity-90'}`}
                          style={{
                            background: isLocked ? "#6B7280" : "#6B3FD8",
                          }}
                          onClick={() => handleLessonClick(lesson)}
                          disabled={isLocked}
                        >
                          {isLocked ? "Заблокировано" : "Слушать"}
                        </button>
                        
                        {isLocked && lesson.premium && !effectivePremium && userId && (
                          <p className="text-xs text-yellow-400 mt-2 text-center">
                            Требуется Premium доступ
                          </p>
                        )}
                        
                        {isLocked && !userId && (
                          <p className="text-xs text-pink-400 mt-2 text-center">
                            Требуется авторизация
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          <div className="h-8"></div>
        </div>
      </div>
    </div>
  );
};

export default AudioLessonsList;